// Client-related filters

import { GeolocationService } from '../../services/GeolocationService';
import {
	createArchivedRecordsFilter,
	createDeletedRecordsFilter,
	createKeywordFilter,
	createRadioFilter,
} from './factories';
import type { FilterRegistry } from './types';

export const clientFilters: FilterRegistry = {
	clientSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by name, address, email, phone, ID...',
			objectTable: 'client',
			listKeys: ['client', 'price_contract_client'],
			supportsRelevance: true, // Uses OpenSearch for relevance
		}),
		queryParamName: 'clientSearch',
	},

	customers: {
		type: 'multi-select',
		label: 'Customers',
		placeholder: 'Type to search customers...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'customers',
		multiple: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: '_id',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['client'],
	},

	// PD-14078 — Child Accounts filter for client-embedded transactional
	// lists (a parent account's Invoices / Orders / Quotes / Transactions
	// tabs). The option collection lazy-loads the viewed client's children
	// via the `{clientId}` context placeholder; the whole filter hides when
	// there are none. Selected children widen the list from parent-only to
	// parent + selection. Because it's a registered filter, saved searches
	// persist the selection.
	//
	// Deliberately NO `queryMapping`: the value is never applied by the
	// generic filter builder — the list services extract `childAccounts` as
	// a context param and resolve it via `expandClientContextFilter`
	// (rewriting `_fk_client__id` to an id array). A queryMapping with
	// objectTable 'client' would also get this filter dropped by
	// validateActiveFilters on the host lists, whose objects are
	// invoice/order/transaction_summary. Scoping to the four host lists is
	// done by `listKeys` instead.
	childAccounts: {
		type: 'multi-select',
		label: 'Child Accounts',
		placeholder: 'Select child accounts...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: false,
		hideWhenNoOptions: true,
		abbreviateSelections: true,
		enableSelectAll: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
			// Option lines under the ID: street, then city/state zip
			detailLines: [
				(item: unknown): string | null => {
					const c = item as {
						address1?: string | null;
						address2?: string | null;
					};
					if (c.address1 == null || c.address1 === '') {
						return null;
					}
					return c.address2 != null && c.address2 !== ''
						? `${c.address1}, ${c.address2}`
						: c.address1;
				},
				(item: unknown): string | null => {
					const c = item as {
						city?: string | null;
						state?: string | null;
						zip?: string | null;
					};
					if (c.city == null || c.city === '' || c.state == null) {
						return null;
					}
					const zip = c.zip != null && c.zip !== '' ? ` ${c.zip}` : '';
					return `${c.city}, ${c.state}${zip}`;
				},
			],
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '200',
			filters: '{"_fk_client__id_parent": {clientId}}',
			fields: JSON.stringify([
				'client.id',
				'client.name',
				'client.externalId',
				'client.address1',
				'client.address2',
				'client.city',
				'client.state',
				'client.zip',
			]),
		},
		// Children listed alphabetically by name (the /clients endpoint
		// ignores a sort param on this call path, so sort client-side)
		sortOptionsByLabel: true,
		// Options carry 4 lines each (name/ID/street/city-state-zip): widen
		// past the 220px sidebar so streets don't wrap, and raise the menu
		// so several accounts are visible at once.
		menuMinWidth: 340,
		menuMaxHeight: 480,
		queryParamName: 'childAccounts',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: [
			'invoice-client-embedded',
			'order-client-embedded',
			'quote-client-embedded',
			'transaction-client-embedded',
		],
	},

	// PD-14186 — Ownership members filter for the Ownership tab
	// (client-ownership-embedded). Options lazy-load the viewed client's
	// ownership siblings via the `{ownershipId}` + `{clientId}` context
	// placeholders (the latter drops the viewed client itself, matching the
	// list's `_fk_ownership__exclude_client` scoping). Unlike childAccounts
	// this DOES carry a queryMapping: the host list is a client list, so a
	// selection narrows it by `_id`.
	ownershipMembers: {
		type: 'multi-select',
		label: 'Ownership Accounts',
		placeholder: 'Select accounts...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: false,
		hideWhenNoOptions: true,
		abbreviateSelections: true,
		enableSelectAll: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
			detailLines: [
				(item: unknown): string | null => {
					const c = item as {
						address1?: string | null;
						address2?: string | null;
					};
					if (c.address1 == null || c.address1 === '') {
						return null;
					}
					return c.address2 != null && c.address2 !== ''
						? `${c.address1}, ${c.address2}`
						: c.address1;
				},
				(item: unknown): string | null => {
					const c = item as {
						city?: string | null;
						state?: string | null;
						zip?: string | null;
					};
					if (c.city == null || c.city === '' || c.state == null) {
						return null;
					}
					const zip = c.zip != null && c.zip !== '' ? ` ${c.zip}` : '';
					return `${c.city}, ${c.state}${zip}`;
				},
			],
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '200',
			filters:
				'{"_fk_ownership__id": {ownershipId}, "_fk_ownership__exclude_client": {clientId}}',
			fields: JSON.stringify([
				'client.id',
				'client.name',
				'client.externalId',
				'client.address1',
				'client.address2',
				'client.city',
				'client.state',
				'client.zip',
			]),
		},
		sortOptionsByLabel: true,
		menuMinWidth: 340,
		menuMaxHeight: 480,
		queryParamName: 'ownershipMembers',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		queryMapping: {
			objectTable: 'client',
			fieldName: '_id',
			joinType: 'direct',
		},
		listKeys: ['client-ownership-embedded'],
	},

	alternateRep: {
		type: 'multi-select',
		label: 'Alternate Salesperson',
		placeholder: 'Search alternate salesperson...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'salesperson_id' as keyof unknown,
			primary: 'display_name' as keyof unknown,
			count: 'client_count' as keyof unknown,
		},
		apiEndpoint: '/users/alternate-reps',
		queryParamName: 'alternateRep',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: '_fk_user__id_alternate',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	clientCompany: {
		type: 'multi-select',
		label: 'Company',
		placeholder: 'Search companies...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		// PD-15698 — most tenants have exactly one company, where a Company
		// filter is meaningless. Multi-company tenants still see it.
		hideWhenSingleOption: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		queryParamName: 'clientCompany',
		apiEndpoint: '/users/companies',
		apiParams: { countFor: 'clients' },
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'exists',
			joinTable: 'client_company',
			joinConditions: {
				sourceField: '_id',
				targetField: 'fk_client__id',
				filterField: 'fk_company__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	businessTypes: {
		type: 'multi-select',
		label: 'Business Types',
		placeholder: 'Search business types...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'business_type' as keyof unknown,
			primary: 'business_type' as keyof unknown,
			count: 'client_count' as keyof unknown,
		},
		apiEndpoint: '/clients/business-types',
		queryParamName: 'businessTypes',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: 'businessType',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client', 'price_contract_client'],
	},

	groups: {
		type: 'multi-select',
		label: 'Groups',
		placeholder: 'Search groups...',
		colorScheme: 'gray',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			count: 'clientCount' as keyof unknown,
		},
		apiEndpoint: '/groups',
		queryParamName: 'groups',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'exists',
			joinTable: 'client_group',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_client__id',
				filterField: '_fk_group__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client', 'price_contract_client'],
	},

	// PD-14228 — Ownership multi-select for the main client list. Not enabled by
	// default; addable via the List Configuration modal. Options come from the
	// /ownerships selector feed (OwnershipDto uses `_id`/`name`). Ownership is a
	// direct FK on the client (a client belongs to at most one ownership), so it
	// maps to a plain `client.fk_ownership__id IN (...)` rather than a join.
	ownership: {
		type: 'multi-select',
		label: 'Ownership',
		placeholder: 'Search ownerships...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: '_id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/ownerships',
		queryParamName: 'ownership',
		multiple: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: 'fk_ownership__id',
			joinType: 'direct',
		},
		format: 'array',
		// transformToFilterOptions normalizes each /ownerships row to an option
		// keyed by `id` (populated from `_id` via displayFields.id above), so the
		// extractor reads `id`, not `_id`. `transform: 'number'` coerces it to the
		// numeric FK and — critically — maps any non-numeric value (e.g. free-typed
		// search text) to null instead of NaN, which otherwise reaches the direct
		// `fk_ownership__id IN (...)` clause as a bareword NaN ("Unknown column
		// 'NaN'"). Mirrors the numeric-FK filters (alternateRep).
		valueExtractor: {
			property: 'id',
			transform: 'number',
		},
		listKeys: ['client'],
	},

	leadOrCustomer: {
		...createRadioFilter({
			label: 'Lead/Customer Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Lead', label: 'Leads' },
				{ id: 'Customer', label: 'Customers' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'client',
			fieldName: 'leadOrCustomer',
			listKeys: ['client', 'price_contract_client'],
		}),
		queryParamName: 'leadOrCustomer',
	},

	lifecycleStage: {
		type: 'multi-select',
		label: 'Lifecycle Stage',
		placeholder: 'Select lifecycle stages...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: '_id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/client-lifecycle-stages',
		queryParamName: 'lifecycleStage',
		multiple: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: 'fk_client_lifecycle_stage__id',
			joinType: 'direct',
		},
		format: 'array',
		valueExtractor: {
			property: '_id',
			transform: 'number',
		},
		listKeys: ['client'],
	},

	// Filters by lifecycle stage name - allows consistent filtering across companies
	// Used by tab defaults and visible in sidebar
	lifecycleStageName: {
		type: 'multi-select',
		label: 'Lifecycle Stage',
		placeholder: 'Select lifecycle stages...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'name' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/client-lifecycle-stages',
		queryParamName: 'lifecycleStageName',
		multiple: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: 'name',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'name',
		},
		listKeys: ['client'],
	},

	callStatus: {
		...createRadioFilter({
			label: 'Call Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Callable', label: 'Callable' },
				{ id: 'Do Not Call', label: 'Do Not Call' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'client',
			fieldName: 'isDoNotCall',
			listKeys: ['client'],
			colorScheme: 'green',
		}),
		queryParamName: 'callStatus',
	},

	// Simple State multi-select (PD-15407) — the default location filter on the
	// clients list, replacing the combined geography search there (geography,
	// nearMe stay registered so users can re-add them via Configure List).
	// Options are the states actually present on the tenant's clients; the
	// backend matches either the raw state column or the standardized code.
	clientState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/clients/state/options',
		queryParamName: 'clientState',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			fieldName: 'state',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	geography: {
		type: 'multi-select',
		label: 'Location',
		placeholder: 'Search US city, county, or state...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: true,
		menuMinWidth: 280,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/locations/search',
		queryParamName: 'geography',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		// 'merchandiser' (PD-12625): the merchandiser map reuses this Location
		// filter; its endpoint applies the geography to the merchandiser's client.
		listKeys: ['client', 'merchandiser'],
	},

	revenue90Days: {
		type: 'multi-select',
		label: '90-Day Revenue',
		placeholder: 'Select revenue range...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'revenue90Days',
		multiple: true,
		options: [
			{ id: 'none', label: 'No Revenue', data: { min: null, max: 0 } },
			{ id: '0-10k', label: '$0 - $10K', data: { min: 0, max: 10000 } },
			{ id: '10k-25k', label: '$10K - $25K', data: { min: 10000, max: 25000 } },
			{ id: '25k-50k', label: '$25K - $50K', data: { min: 25000, max: 50000 } },
			{
				id: '50k-100k',
				label: '$50K - $100K',
				data: { min: 50000, max: 100000 },
			},
			{
				id: '100k-250k',
				label: '$100K - $250K',
				data: { min: 100000, max: 250000 },
			},
			{
				id: '250k-500k',
				label: '$250K - $500K',
				data: { min: 250000, max: 500000 },
			},
			{
				id: '500k-1m',
				label: '$500K - $1M',
				data: { min: 500000, max: 1000000 },
			},
			{ id: '1m-plus', label: '$1M+', data: { min: 1000000, max: null } },
		],
		queryMapping: {
			objectTable: 'client',
			fieldName: 'rollingRevenue90',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	orderSalesStatus: {
		type: 'multi-select',
		label: 'Order Status',
		placeholder: 'Select order status...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
			count: 'count' as keyof unknown,
		},
		apiEndpoint: '/orders/status-counts',
		queryParamName: 'orderSalesStatus',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	specialFilters: {
		type: 'single-select',
		label: 'Special Filters',
		placeholder: 'Select a special filter...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'specialFilters',
		multiple: false,
		allowClear: true,
		options: [
			{ id: 'scheduled_today', label: 'Scheduled Today' },
			{ id: 'overdue', label: 'Overdue' },
			{ id: 'contact_due', label: 'Contact Due' },
			{ id: 'suggested_sales', label: 'Suggested Sales' },
			{ id: 'at_risk', label: 'At Risk' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	reorderDue: {
		type: 'single-select',
		label: 'Reorder Due',
		placeholder: 'Select reorder status...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'reorderDue',
		multiple: false,
		allowClear: true,
		options: [
			{ id: 'overdue', label: 'Overdue' },
			{ id: 'thisWeek', label: 'Due This Week' },
			{ id: 'thisMonth', label: 'Due This Month' },
			{ id: 'any', label: 'Has Reorder Date' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	/**
	 * PD-15443 - customer retention buckets over `client.lastOrderDate`.
	 *
	 * The four option ids are the buckets ClientsService._applyExtendedFilters
	 * already implements, so this entry exposes existing server logic rather
	 * than adding SQL. Each bucket is a date-range expression rather than a
	 * column comparison, which is why the value is applied there instead of by
	 * the generic builder - see SERVICE_APPLIED_FILTERS in typeormFilterBuilder.
	 * The queryMapping still has to name `client` so parseFilterParams keeps the
	 * value for this list at all.
	 *
	 * The service's `lapsed` bucket (60+ days, atRisk and inactive combined) is
	 * deliberately not offered here: it exists for the day planner's single-query
	 * at-risk signal and would overlap the two buckets beside it.
	 *
	 * Clients that have never ordered (NULL lastOrderDate) match no bucket by
	 * design. Every bucket also forces leadOrCustomer='Customer' (PD-14918), so
	 * a lead that has never ordered is not a retention risk.
	 */
	lastOrderStatus: {
		type: 'single-select',
		label: 'Last Order Activity',
		placeholder: 'Select last order activity...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'lastOrderStatus',
		multiple: false,
		allowClear: true,
		options: [
			{ id: 'recent', label: 'Last 30 Days' },
			{ id: 'stale', label: '30-60 Days' },
			{ id: 'atRisk', label: '60-90 Days' },
			{ id: 'inactive', label: '90+ Days' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	nearMe: {
		type: 'proximity',
		label: 'Near',
		placeholder: 'Search client or location...',
		colorScheme: 'blue',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'nearMe',
		multiple: false,
		allowClear: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			// Pass full object to customTransform so it can access the 'data' property
			extract: (item: unknown) => item,
		},
		customTransform: (value: unknown): unknown => {
			if (value == null) {
				return null;
			}

			// Handle new proximity filter format with data object
			if (Array.isArray(value) && value.length > 0) {
				const item = value[0] as {
					data?: {
						centerType?: string;
						lat?: number;
						lng?: number;
						radius?: number;
					};
				};
				if (item?.data != null) {
					const data = item.data;
					if (data.centerType === 'me') {
						const position = GeolocationService.getCachedPosition();
						if (position == null) {
							return null;
						}
						return `${String(position.lat)},${String(position.lng)},${String(data.radius ?? 25)}`;
					}
					if (data.lat != null && data.lng != null && data.radius != null) {
						return `${String(data.lat)},${String(data.lng)},${String(data.radius)}`;
					}
					return null;
				}
				// Legacy format: array of string IDs
				const radius = String(value[0]);
				const position = GeolocationService.getCachedPosition();
				if (position == null) {
					return null;
				}
				return `${String(position.lat)},${String(position.lng)},${radius}`;
			}

			// Legacy format: single string value (radius)
			if (typeof value === 'string') {
				const position = GeolocationService.getCachedPosition();
				if (position == null) {
					return null;
				}
				return `${String(position.lat)},${String(position.lng)},${value}`;
			}

			return null;
		},
		listKeys: ['client', 'price_contract_client'],
	},

	salesStatus: {
		...createRadioFilter({
			label: 'Sales Status',
			options: [
				{ id: 'All', label: 'All' },
				{ id: 'Active', label: 'Active' },
				{ id: 'Inactive', label: 'Inactive' },
			],
			defaultValue: { id: 'All', label: 'All' },
			objectTable: 'client',
			fieldName: 'salesStatus',
			listKeys: ['client', 'price_contract_client'],
			colorScheme: 'green',
		}),
		queryParamName: 'salesStatus',
	},

	clientLists: {
		type: 'multi-select',
		label: 'Client Lists',
		placeholder: 'Search client lists...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: '_id' as keyof unknown,
			primary: 'name' as keyof unknown,
			count: 'clientCount' as keyof unknown,
		},
		apiEndpoint: '/clients/client-lists',
		searchFilterName: 'search',
		apiParams: {
			page: '1',
			limit: '25',
		},
		queryParamName: 'clientLists',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'exists',
			joinTable: 'client_list__client',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_client__id',
				filterField: '_fk_client_list__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: '_id',
		},
		listKeys: ['client'],
	},

	excludeClientLists: {
		type: 'multi-select',
		label: 'Exclude Client Lists',
		placeholder: 'Exclude lists...',
		colorScheme: 'red',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: '_id' as keyof unknown,
			primary: 'name' as keyof unknown,
			count: 'clientCount' as keyof unknown,
		},
		apiEndpoint: '/clients/client-lists',
		searchFilterName: 'search',
		apiParams: {
			page: '1',
			limit: '25',
		},
		queryParamName: 'excludeClientLists',
		multiple: true,
		// PD-14109: count reflects the currently filtered Clients list.
		scopeCountsToView: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'not_exists',
			joinTable: 'client_list__client',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_client__id',
				filterField: '_fk_client_list__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: '_id',
		},
		listKeys: ['client'],
	},

	// Internal filter used by the "Add Clients to Group" modal to exclude
	// clients that are already members of a given group
	excludeGroups: {
		type: 'multi-select',
		label: 'Exclude Groups',
		placeholder: 'Exclude groups...',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		queryParamName: 'excludeGroups',
		multiple: true,
		queryMapping: {
			objectTable: 'client',
			joinType: 'not_exists',
			joinTable: 'client_group',
			joinConditions: {
				sourceField: '_id',
				targetField: '_fk_client__id',
				filterField: '_fk_group__id',
			},
		},
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	// Programmatic filter: constrains client list to clients in a specific price contract
	// Not user-facing — injected by the price contract controller
	priceContractId: {
		type: 'text',
		label: 'Price Contract',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'priceContractId',
		multiple: false,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client', 'price_contract_client'],
	},

	// Programmatic filter: excludes clients already on a specific price contract
	// Not user-facing — injected by the price contract "add clients" picker
	notInPriceContractId: {
		type: 'text',
		label: 'Not in Price Contract',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'notInPriceContractId',
		multiple: false,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	// PD-14228 — programmatic filter: constrains the client list to clients in a
	// specific ownership. Injected server-side by the ownership detail Clients
	// tab; it must be REGISTERED so parseFilterParams routes it, but it is NOT a
	// user-selectable filter. The sentinel listKey keeps it out of every List
	// Configuration "available filters" pool (parseFilterParams ignores
	// listKeys, so backend injection is unaffected) — otherwise it would show as
	// a confusing second "Ownership" text filter next to the real multi-select.
	ownershipId: {
		type: 'text',
		label: 'Ownership',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'ownershipId',
		multiple: false,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['__ownership_constraint_internal__'],
	},

	// PD-14228 — programmatic filter: excludes clients already in a specific
	// ownership (and includes unassigned clients). Injected server-side by the
	// ownership detail "Add Clients" picker; sentinel listKey hides it from user
	// pools (see ownershipId above).
	notInOwnershipId: {
		type: 'text',
		label: 'Not in Ownership',
		placeholder: '',
		colorScheme: 'gray',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'notInOwnershipId',
		multiple: false,
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['__ownership_constraint_internal__'],
	},

	clientHistoryKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search by item name, manufacturer...',
			objectTable: 'client',
			listKeys: ['clientItemHistory'],
		}),
		queryParamName: 'clientHistoryKeywords',
	},

	clientDeletedRecords: createDeletedRecordsFilter({
		objectTable: 'client',
		listKey: 'client',
	}),

	// PD-15452 — surface archived clients on demand; lists hide them by default.
	clientArchivedRecords: createArchivedRecordsFilter({
		objectTable: 'client',
		listKey: 'client',
	}),

	/**
	 * PD-15504 — find the clients a route covers, or the ones it does not.
	 *
	 * 'Live' matches the Routes column and the orders Routing list: a client
	 * whose only route is paused, expired or archived counts as NOT routed,
	 * because none of those will dispatch. Answering otherwise would hide
	 * exactly the clients this filter exists to surface.
	 */
	clientRouted: {
		...createRadioFilter({
			// PD-15924 (decision 4) — "Delivery" now that sales routes have their
			// own Routed filter below. Filter id and query param are unchanged so
			// saved layouts and links keep working.
			label: 'Delivery Routed',
			objectTable: 'client',
			fieldName: 'routed',
			listKeys: ['client'],
			options: [
				{ id: 'yes', label: 'Delivery Routed' },
				{ id: 'no', label: 'Not Delivery Routed' },
			],
		}),
		queryParamName: 'clientRouted',
		// The mapping has to name `client` so parseFilterParams keeps the value
		// for the clients list — but there is no `client.routed` column. The
		// condition spans route_client and route, so ClientsService applies it
		// as an EXISTS and the shared SQL builder skips it by name (see
		// SERVICE_APPLIED_FILTERS in typeormFilterBuilder).
	},

	/**
	 * PD-15924 — the sales-route counterpart of Delivery Routed: is the client
	 * on a LIVE sales route (active and unexpired)? Same EXISTS shape, applied
	 * by ClientsService (see SERVICE_APPLIED_FILTERS in typeormFilterBuilder).
	 */
	clientSalesRouted: {
		...createRadioFilter({
			label: 'Sales Routed',
			objectTable: 'client',
			fieldName: 'salesRouted',
			listKeys: ['client'],
			options: [
				{ id: 'yes', label: 'Sales Routed' },
				{ id: 'no', label: 'Not Sales Routed' },
			],
		}),
		queryParamName: 'clientSalesRouted',
	},

	/**
	 * PD-15698 — "who hasn't been on a trip lately?" as open-ended ≥-day
	 * buckets over the client's last COMPLETED trip stop, mirroring the Last
	 * Order Activity filter's single-select shape. Each N+ bucket INCLUDES
	 * never-visited clients (their days-since is unbounded — hiding them from
	 * a "not visited in 30+ days" answer would hide the worst offenders);
	 * 'never' narrows to exactly those. Applied by ClientsService against the
	 * lastStop join (see SERVICE_APPLIED_FILTERS in typeormFilterBuilder).
	 */
	lastStopDays: {
		type: 'single-select',
		label: 'Days Since Last Stop',
		placeholder: 'Select days since last stop...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'lastStopDays',
		multiple: false,
		allowClear: true,
		options: [
			{ id: '7', label: '7+ Days' },
			{ id: '14', label: '14+ Days' },
			{ id: '21', label: '21+ Days' },
			{ id: '30', label: '30+ Days' },
			{ id: '60', label: '60+ Days' },
			{ id: '90', label: '90+ Days' },
			{ id: 'never', label: 'Never Visited' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	/**
	 * PD-15918 — "who hasn't a rep seen lately?" as ≥-day buckets over the
	 * denormalised `client.lastVisitDate` (decision 3: no join). Same shape and
	 * semantics as Days Since Last Stop: each N+ bucket INCLUDES never-visited
	 * clients, 'never' narrows to exactly those. Applied by ClientsService (see
	 * SERVICE_APPLIED_FILTERS in typeormFilterBuilder).
	 */
	lastVisitDays: {
		type: 'single-select',
		label: 'Last Visit',
		placeholder: 'Select days since last visit...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'lastVisitDays',
		multiple: false,
		allowClear: true,
		options: [
			{ id: '7', label: '7+ Days' },
			{ id: '30', label: '30+ Days' },
			{ id: '60', label: '60+ Days' },
			{ id: '90', label: '90+ Days' },
			{ id: 'never', label: 'Never Visited' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},

	/**
	 * PD-15918 — the delivery counterpart over `client.lastDeliveryDate`, the
	 * GREATEST of completed trip stop / shipment / invoice dates (decision 18).
	 */
	lastDeliveryDays: {
		type: 'single-select',
		label: 'Last Delivery',
		placeholder: 'Select days since last delivery...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'lastDeliveryDays',
		multiple: false,
		allowClear: true,
		options: [
			{ id: '7', label: '7+ Days' },
			{ id: '30', label: '30+ Days' },
			{ id: '60', label: '60+ Days' },
			{ id: '90', label: '90+ Days' },
			{ id: 'never', label: 'Never Delivered' },
		],
		queryMapping: {
			objectTable: 'client',
			joinType: 'custom',
		},
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client'],
	},
};
